/**
 * Channel Receiver Engine — the payee-side streaming state machine.
 *
 * States: idle → negotiating → streaming → closing → settling → settled | error
 *
 * Receives bilateral signed states from the sender, validates, cosigns, responds.
 */

import { canonicalizeQr20Json, hashQr20Payload } from '@qr20/envelope';
import { computeIntentDigest, canonicaliseStateUpdateForSigning, validateExtendedChannel, classifyStateUpdateReplay } from '@qr20/channel';
import { type Qr20WebrtcSessionContract } from '@tokenstreaming/transport-webrtc';
import { SmoothDisplayEngine } from './smoothDisplay.js';
import { buildDistributionIntent } from './distributionBuilder.js';
import { OnChainService } from './onChainService.js';
import type { StreamParams, ChannelState, EngineCallbacks, EngineState, StreamingEngine, DistributionParams } from './types.js';

export class ChannelReceiverEngine implements StreamingEngine {
  private state: EngineState = 'idle';
  private channelState: ChannelState | null = null;
  private params: StreamParams;
  private callbacks: EngineCallbacks;
  private session: Qr20WebrtcSessionContract;
  private signMessage: (message: string) => Promise<string>;
  private smoothDisplay = new SmoothDisplayEngine();
  private intentDigest: string = '0x' + '00'.repeat(32);
  private startTime = 0;
  private stopped = false;
  private onChainService: OnChainService;

  constructor(
    params: StreamParams,
    session: Qr20WebrtcSessionContract,
    signMessage: (message: string) => Promise<string>,
    callbacks: EngineCallbacks
  ) {
    this.params = params;
    this.session = session;
    this.signMessage = signMessage;
    this.callbacks = callbacks;
    this.onChainService = new OnChainService(params.chain);
  }

  async start(): Promise<void> {
    if (this.state !== 'idle') throw new Error(`Cannot start from state: ${this.state}`);

    this.setState('negotiating');
    this.startTime = Date.now();

    console.log('[ChReceiver] start()', {
      channelId: this.params.channelId,
      creator: this.params.creatorAddress,
      resolver: this.params.resolverAddress,
    });

    try {
      // Wait for channel-open from sender
      await this.waitForChannelOpen();

      console.log('[ChReceiver] channel-open received → sending channel-accept');

      // Send accept with initial cosigned state
      await this.sendAccept();

      console.log('[ChReceiver] channel-accept sent → transitioning to streaming');

      // Start receiving state updates
      this.setState('streaming');
      this.smoothDisplay.reset(0n, this.params.ratePerSecAtomic);
      this.startReceiveLoop();
    } catch (err) {
      console.error('[ChReceiver] start() failed:', err);
      this.handleError(err instanceof Error ? err : new Error(String(err)));
    }
  }

  async stop(): Promise<void> {
    this.stopped = true;
    this.smoothDisplay.pause();
  }

  async close(): Promise<void> {
    if (this.state !== 'streaming') {
      if (this.state === 'settled' || this.state === 'error') return;
    }

    this.setState('closing');

    try {
      // Send close intent to sender
      await this.sendCloseIntent();
      this.setState('settled');
      if (this.channelState) {
        this.callbacks.onSettled(''); // No on-chain tx yet; sender submits
      }
    } catch (err) {
      this.handleError(err instanceof Error ? err : new Error(String(err)));
    }
  }

  getState(): EngineState {
    return this.state;
  }

  getChannelState(): ChannelState | null {
    return this.channelState;
  }

  getRole(): 'receiver' {
    return 'receiver';
  }

  getElapsedMs(): number {
    return Date.now() - this.startTime;
  }

  getSmoothAmount(): bigint {
    return this.smoothDisplay.getCurrentAmount();
  }

  // ─── Private: Negotiation ─────────────────────────────────────────────────

  private async waitForChannelOpen(): Promise<void> {
    const timeout = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Channel open timeout (30s)')), 30_000);
    });

    const waitForMessage = async (): Promise<void> => {
      console.log('[ChReceiver] waitForChannelOpen: listener attached, awaiting channel-open');
      for await (const envelope of this.session.receive()) {
        if (this.stopped) return;
        const msg = envelope as any;
        console.log('[ChReceiver] waitForChannelOpen: received envelope', { id: msg?.id });
        const intentData = msg?.intent?.task?.input_data;
        if (intentData) {
          try {
            const parsed = JSON.parse(intentData);
            console.log('[ChReceiver] waitForChannelOpen: parsed intent type =', parsed.type);
            if (parsed.type === 'channel-open') {
              // Validate and store channel params
              this.params = {
                ...this.params,
                channelId: parsed.channelId ?? this.params.channelId,
                deposit: BigInt(parsed.deposit ?? this.params.deposit.toString()),
                disputeWindowS: parsed.disputeWindowS ?? this.params.disputeWindowS,
                ratePerSecAtomic: BigInt(parsed.ratePerSecAtomic ?? this.params.ratePerSecAtomic.toString()),
              };

              // Initialize at sequence 0
              this.channelState = {
                channelId: this.params.channelId,
                sequence: 0,
                creatorAmount: this.params.deposit,
                resolverAmount: 0n,
                intentDigest: this.intentDigest,
                lastUpdateTime: Date.now(),
              };
              this.callbacks.onBalanceUpdate(this.channelState);
              return;
            }
          } catch {
            // Not a channel-open, continue
          }
        }
      }
    };

    await Promise.race([waitForMessage(), timeout]);
  }

  private async sendAccept(): Promise<void> {
    const acceptPayload = {
      type: 'channel-accept',
      channelId: this.params.channelId,
      sequence: 0,
    };

    const acceptMsg = this.buildEnvelope(acceptPayload);
    acceptMsg.id = await this.computeContentId(acceptMsg);
    console.log('[ChReceiver] sending channel-accept envelope', { id: acceptMsg.id });
    await this.session.send(acceptMsg as any);
    console.log('[ChReceiver] channel-accept sent successfully');
  }

  // ─── Private: Streaming ───────────────────────────────────────────────────

  private startReceiveLoop(): void {
    void (async () => {
      try {
        for await (const envelope of this.session.receive()) {
          if (this.stopped) break;
          await this.handleReceivedMessage(envelope as any);
        }
      } catch {
        if (!this.stopped && this.state === 'streaming') {
          this.handleError(new Error('Session closed unexpectedly'));
        }
      }
    })();
  }

  private async handleReceivedMessage(msg: any): Promise<void> {
    console.log('[ChReceiver] handleReceivedMessage envelope id=', msg?.id);
    const intentData = msg?.intent?.task?.input_data;
    if (!intentData) return;

    let parsed: any;
    try {
      parsed = JSON.parse(intentData);
    } catch {
      return;
    }
    console.log('[ChReceiver] handleReceivedMessage type=', parsed.type);

    if (parsed.type === 'channel-update') {
      await this.handleStateUpdate(parsed);
    } else if (parsed.type === 'channel-close') {
      await this.close();
    }
  }

  private async handleStateUpdate(update: any): Promise<void> {
    if (!this.channelState) return;

    const newSequence = update.sequence as number;
    const creatorAmount = BigInt(update.balances?.creator_amount ?? '0');
    const resolverAmount = BigInt(update.balances?.resolver_amount ?? '0');
    const incomingDigest = update.intent_digest as string;

    // Validate monotonicity
    if (newSequence <= this.channelState.sequence) {
      // Stale or duplicate — ignore
      return;
    }

    // Validate balance conservation
    if (creatorAmount + resolverAmount !== this.params.deposit) {
      this.handleError(new Error('Balance conservation violation from sender'));
      return;
    }

    // Validate balances are non-negative and resolver amount only increases
    if (resolverAmount < this.channelState.resolverAmount) {
      this.handleError(new Error('Resolver amount decreased — protocol violation'));
      return;
    }

    // Compute our own rolling intent digest and verify
    const statePayload = {
      channelId: this.params.channelId,
      sequence: newSequence,
      creatorAmount: creatorAmount.toString(),
      resolverAmount: resolverAmount.toString(),
    };
    this.intentDigest = await computeIntentDigest(
      [JSON.parse(JSON.stringify(statePayload))],
      this.intentDigest
    );

    // Cosign the state update
    const stateForSigning = {
      channel_id: this.params.channelId,
      sequence: newSequence,
      balances: {
        creator_amount: creatorAmount.toString(),
        resolver_amount: resolverAmount.toString(),
      },
      intent_digest: this.intentDigest,
      creator_sig: { scheme: 'secp256k1-eip191' as const, value: update.creator_sig?.value ?? '0x' },
      resolver_sig: { scheme: 'secp256k1-eip191' as const, value: '0x' },
    };

    const canonBytes = canonicaliseStateUpdateForSigning(stateForSigning);
    const cosignature = await this.signMessage(canonBytes);

    // Send cosigned ack back
    const ackPayload = {
      type: 'channel-update-ack',
      channelId: this.params.channelId,
      sequence: newSequence,
      resolver_sig: {
        scheme: 'secp256k1-eip191',
        value: cosignature,
      },
    };

    const ackMsg = this.buildEnvelope(ackPayload);
    ackMsg.id = await this.computeContentId(ackMsg);
    await this.session.send(ackMsg as any);

    // Update local state
    this.channelState = {
      channelId: this.params.channelId,
      sequence: newSequence,
      creatorAmount,
      resolverAmount,
      intentDigest: this.intentDigest,
      lastUpdateTime: Date.now(),
    };
    this.callbacks.onBalanceUpdate(this.channelState);
    this.smoothDisplay.sync(resolverAmount);
  }

  // ─── Private: Close ───────────────────────────────────────────────────────

  private async sendCloseIntent(): Promise<void> {
    if (!this.channelState) return;

    const closePayload = {
      type: 'channel-close',
      channelId: this.params.channelId,
      sequence: this.channelState.sequence,
      creatorAmount: this.channelState.creatorAmount.toString(),
      resolverAmount: this.channelState.resolverAmount.toString(),
      intentDigest: this.channelState.intentDigest,
    };

    const closeMsg = this.buildEnvelope(closePayload);
    closeMsg.id = await this.computeContentId(closeMsg);
    await this.session.send(closeMsg as any);
  }

  // ─── Private: Helpers ─────────────────────────────────────────────────────

  private async computeContentId(envelope: Record<string, unknown>): Promise<string> {
    const stripped: Record<string, unknown> = { ...envelope };
    delete stripped.id;
    delete stripped.proof;
    if (typeof stripped.creator === 'object' && stripped.creator !== null) {
      stripped.creator = { ...(stripped.creator as Record<string, unknown>), signature: '' };
    }
    const hash = await hashQr20Payload(stripped, {
      crypto: { subtle: globalThis.crypto.subtle },
      algorithm: 'SHA-256',
    });
    return `sha256:${hash}`;
  }

  private buildEnvelope(intent: Record<string, unknown>): Record<string, unknown> {
    return {
      qr: '20',
      version: '1.0.0',
      type: 11,
      id: '', // computed after build via computeContentId
      timestamp: new Date().toISOString(),
      creator: {
        address: this.params.resolverAddress,
        signature: '',
        chain: this.params.chain,
      },
      routing: {
        mode: 'direct',
        target_resolvers: [this.params.creatorAddress],
        required_capabilities: ['channel.stream'],
        deadline: new Date(Date.now() + 3600_000).toISOString(),
        max_fee: '0',
      },
      encryption: { method: 'none', params: {} },
      intent: {
        action: 'agent_request',
        sender_agent: { id: 'receiver', owner: this.params.resolverAddress, delegation_proof: 'self' },
        recipient_agent: { id: 'sender', owner: this.params.creatorAddress },
        task: {
          description: JSON.stringify(intent),
          constraints: {
            max_cost: { amount: '0', token: this.params.asset },
            deadline: new Date(Date.now() + 3600_000).toISOString(),
            required_proofs: ['on_chain_hash'],
          },
          input_data: JSON.stringify(intent),
          output_schema: { ack: 'boolean' },
        },
        verification: { method: 'on_chain_hash', arbitration: this.params.resolverAddress },
      },
      channel: {
        channel_id: this.params.channelId,
        sequence: this.channelState?.sequence ?? 0,
      },
      extensions: {},
    };
  }

  private setState(state: EngineState): void {
    this.state = state;
    this.callbacks.onStateChange(state);
  }

  private handleError(error: Error): void {
    this.setState('error');
    this.callbacks.onError(error);
  }
}
